import { apiCustomer } from "@/api"
import type { OrderResponse } from "@/types/external"
import { isAxiosError } from "axios"

/** The customer's stored rating of one order, as customer-service-gateway returns it. */
export type OrderFeedback = {
  id: string
  orderId: string
  source: "webpage" | "whatsapp" | string
  stars: number
  comment: string
  createdAt: string
}

/** Mirrors order-service-core's FeedbackWindow; the server stays the authority. */
export const FEEDBACK_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
export const MAX_COMMENT_LENGTH = 500

/** Why a submission failed, mapped from the order core's reason codes. */
export type FeedbackFailure = "notDelivered" | "windowExpired" | "failed"

/**
 * Delivered (DELIVERED/CONCLUDED, or PICKED_UP for pickup and table orders) and inside the 30-day window.
 * A delivery order's PICKED_UP is the rider collecting it, so it is not rateable yet.
 */
export function isRateable(order: OrderResponse, now: number = Date.now()): boolean {
  const last = order.events?.[order.events.length - 1]
  if (!last) return false
  const pickup = order.type === "TAKEOUT" || order.type === "INDOOR"
  const delivered = last.status === "DELIVERED" || last.status === "CONCLUDED" || (pickup && last.status === "PICKED_UP")
  if (!delivered) return false
  const at = Date.parse(last.createdAt)
  return Number.isFinite(at) && now - at <= FEEDBACK_WINDOW_MS
}

/** The customer's ratings of the given orders at the store, keyed by order id. */
export async function fetchOrderFeedbacks(merchantId: string, orderIds: string[]): Promise<Record<string, OrderFeedback>> {
  if (orderIds.length === 0) return {}
  const { data } = await apiCustomer.get<OrderFeedback[]>(`/orders/merchants/${merchantId}/feedbacks`, {
    params: { orderIds: orderIds.join(",") },
  })
  const byOrder: Record<string, OrderFeedback> = {}
  for (const fb of data ?? []) {
    if (!byOrder[fb.orderId]) byOrder[fb.orderId] = fb
  }
  return byOrder
}

/** Outcome of a submission: the stored rating and whether the order was already rated (one rating per order, the first wins). */
export type FeedbackSubmission = { feedback: OrderFeedback; alreadyRated: boolean }

/** Sends the rating; an already rated order returns the stored one with alreadyRated. Throws a FeedbackFailure on refusal. */
export async function submitOrderFeedback(orderId: string, stars: number, comment: string): Promise<FeedbackSubmission> {
  try {
    const { data } = await apiCustomer.post<{ feedback: OrderFeedback; created: boolean; alreadyRated?: boolean }>(`/orders/${orderId}/feedback`, {
      stars,
      comment: comment.trim().slice(0, MAX_COMMENT_LENGTH),
    })
    return { feedback: data.feedback, alreadyRated: data.alreadyRated ?? !data.created }
  } catch (error) {
    throw feedbackFailureOf(error)
  }
}

/** Maps a failed submission to the copy key the sheet shows. */
export function feedbackFailureOf(error: unknown): FeedbackFailure {
  if (!isAxiosError(error)) return "failed"
  const code = (error.response?.data as { code?: string } | undefined)?.code
  if (code === "ORD-FBK-4001") return "notDelivered"
  if (code === "ORD-FBK-4002") return "windowExpired"
  return "failed"
}
